import { useContext, useEffect, useState } from "react";

//material-ui
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Link, TextField, Switch, Typography, Select, MenuItem } from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";

//providers/hooks
import { MqttContext } from "../../providers/MqttProvider";
import { useAppSelector } from "../../hooks/reduxHooks";

//helpers
import { timestampFormatDateTime } from "../../helpers/timestampFormat";

interface DeviceSettingsProps {
	Device: Device;
	refetch: () => void;
}

const DeviceSettings = ({ Device, refetch }: DeviceSettingsProps) => {
	const { mqttPublish, mqttConnected } = useContext(MqttContext);
	const [open, setOpen] = useState(false);
	const [UpdateInterval, setUpdateInterval] = useState<number | string>("---");
	const [Title, setTitle] = useState("---");
	const [BreakBeamMin, setBreakBeamMin] = useState<number | string>("---");
	const [TZ, setTZ] = useState<number | string>("");
	const [Debug, setDebug] = useState(false);
	const [Demo, setDemo] = useState(false);
	const [FromDeviceInit, setFromDeviceInit] = useState(false);

	const FromDevice = useAppSelector((state) => state.device[Device.DeviceID]);

	useEffect(() => {
		setTitle(Device.Title || "---");
		setUpdateInterval(Device.UpdateInterval || "---");
		setBreakBeamMin(Device.BreakBeamMin || "---");
		setTZ(Device.TZ || "---");
		setDebug(Device.Debug ? true : false);
		setDemo(Device.Demo ? true : false);
		mqttPublish(`/countc/${Device.DeviceID}/command`, '{ "cmdType": "reportSettings" }');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (FromDeviceInit) {
			console.log("FromDeviceChanged", FromDevice);
			refetch();
		}
		if (FromDevice) {
			setFromDeviceInit(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [FromDevice?.Title, FromDevice?.UpdateInterval, FromDevice?.BreakBeamMin, FromDevice?.TZ, FromDevice?.Debug, FromDevice?.Demo]);

	const handleClickOpen = () => {
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
	};

	const handleGetDeviceSettings = () => {
		if (mqttConnected) {
			mqttPublish(`/countc/${Device.DeviceID}/command`, '{ "cmdType": "reportSettings" }');
		}
	};

	const handleUpdateSettings = () => {
		mqttPublish(
			`/countc/${Device.DeviceID}/command`,
			`{ "cmdType": "setSettings", "UpdateInterval": ${UpdateInterval}, "DeviceTitle": "${Title}", "BreakBeamMin": ${BreakBeamMin}, "TZ": "${TZ}", "Debug": ${Debug} , "Demo": ${Demo}}`
		);
	};

	const handleDebugChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setDebug(event.target.checked);
	};

	const handleDemoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setDemo(event.target.checked);
	};

	return (
		<div>
			<Button size="small" variant="outlined" onClick={handleClickOpen} startIcon={<SettingsIcon />}>
				Settings
			</Button>
			<Dialog open={open} onClose={handleClose}>
				<DialogTitle>
					Device Settings: {Device?.Title} <Button onClick={() => handleGetDeviceSettings()}>Update</Button>
				</DialogTitle>
				<DialogContent>
					<Grid container direction="row" justifyContent="stretch" alignItems="stretch" columnSpacing={1}>
						<Grid item xs={6}>
							Update
						</Grid>
						<Grid item xs={6}>
							Device Values
						</Grid>
						<Grid item xs={6}>
							<TextField margin="dense" value={Title} onChange={(e) => setTitle(e.target.value)} label="Title" type="text" fullWidth variant="outlined" />
						</Grid>
						<Grid item xs={6}>
							<TextField margin="dense" value={FromDevice?.Title || "---"} label="Title" type="text" fullWidth variant="outlined" disabled />
						</Grid>

						<Grid item xs={6}>
							<TextField
								margin="dense"
								value={UpdateInterval}
								onChange={(e) => setUpdateInterval(e.target.value.replace(/[^0-9]/g, ""))}
								label="Update Interval"
								type="text"
								variant="outlined"
								fullWidth
							/>
						</Grid>
						<Grid item xs={6}>
							<TextField margin="dense" value={FromDevice?.UpdateInterval || "---"} label="Update Interval" type="text" fullWidth variant="outlined" disabled />
						</Grid>
						<Grid item xs={6}>
							<TextField
								margin="dense"
								value={BreakBeamMin}
								onChange={(e) => setBreakBeamMin(e.target.value.replace(/[^0-9]/g, ""))}
								label="Break Beam Min"
								type="text"
								variant="outlined"
								fullWidth
							/>
						</Grid>
						<Grid item xs={6}>
							<TextField margin="dense" value={FromDevice?.BreakBeamMin || "---"} label="Break Beam Min" type="text" fullWidth variant="outlined" disabled />
						</Grid>

						<Grid item xs={6}>
							<Select value={TZ} label="Time Zone" onChange={(e) => setTZ(Number(e.target.value))} fullWidth>
								<MenuItem value="---">Select TZ</MenuItem>
								<MenuItem value="0">UTC</MenuItem>
								<MenuItem value="1">EDT</MenuItem>
								<MenuItem value="2">CDT</MenuItem>
								<MenuItem value="3">MDT</MenuItem>
								<MenuItem value="4">PNT</MenuItem>
								<MenuItem value="5">PDT</MenuItem>
								<MenuItem value="6">AKDT</MenuItem>
								<MenuItem value="7">HST</MenuItem>
							</Select>
						</Grid>
						<Grid item xs={6}>
							<Select value={FromDevice?.TZ} label="Time Zone" fullWidth disabled>
								<MenuItem value="---">Select TZ</MenuItem>
								<MenuItem value="0">UTC</MenuItem>
								<MenuItem value="1">EDT</MenuItem>
								<MenuItem value="2">CDT</MenuItem>
								<MenuItem value="3">MDT</MenuItem>
								<MenuItem value="4">PNT</MenuItem>
								<MenuItem value="5">PDT</MenuItem>
								<MenuItem value="6">AKDT</MenuItem>
								<MenuItem value="7">HST</MenuItem>
							</Select>{" "}
						</Grid>
						<Grid item xs={6}>
							Debug <Switch checked={Debug ? true : false} onChange={handleDebugChange} inputProps={{ "aria-label": "controlled" }} />
						</Grid>
						<Grid item xs={6}>
							Debug <Switch checked={FromDevice?.Debug ? true : false} disabled />
						</Grid>
						<Grid item xs={6}>
							Demo Mode <Switch checked={Demo ? true : false} onChange={handleDemoChange} inputProps={{ "aria-label": "controlled" }} />
						</Grid>
						<Grid item xs={6}>
							Demo Mode <Switch checked={FromDevice?.Demo ? true : false} disabled />
						</Grid>
						<Grid item xs={6}>
							<Button variant="contained" fullWidth onClick={handleUpdateSettings}>
								Submit
							</Button>
						</Grid>
					</Grid>

					<Box style={{ marginTop: "20px" }}>
						<Grid container spacing={2}>
							<Grid item xs={6}>
								<Typography>
									IP Address:{" "}
									<Link href={`http://${Device?.IpAddress}`} target="_blank" rel="noreferrer">
										{Device?.IpAddress}{" "}
									</Link>
								</Typography>
							</Grid>
							<Grid item xs={6}>
								<Typography>MAC Address: {Device?.MacAddress}</Typography>
							</Grid>
							<Grid item xs={6}>
								<Typography>Updated: {timestampFormatDateTime(Number(Device?.UpdateDate))}</Typography>
							</Grid>
							<Grid item xs={6}>
								<Typography>Created: {timestampFormatDateTime(Number(Device?.CreateDate))}</Typography>
							</Grid>
							<Grid item xs={6}>
								<Typography>Device Time: {timestampFormatDateTime(Number(FromDevice?.DeviceTime), 1000)}</Typography>
							</Grid>
							<Grid item xs={6}>
								<Typography>Last Message: {FromDevice?.msgType}</Typography>
							</Grid>
							<Grid item xs={6}>
								<Typography>Firmware Ver: {Device?.Version}</Typography>
							</Grid>
							<Grid item xs={6}>
								<Typography>DeviceID: {Device?.DeviceID}</Typography>
							</Grid>
						</Grid>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose}>Close</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default DeviceSettings;
